import React from 'react';

/** Declared structurally so the generated query types satisfy it without a cast. */
export interface PaginatedConnection<TNode> {
  edges: { node: TNode }[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
  totalCount: number;
}

export interface ConnectionPagination<TNode> {
  rows: TNode[];
  hasNextPage: boolean;
  totalCount: number;
  loadMore: () => void;
  isFetchingMore: boolean;
  /**
   * Re-reads the pages already on screen under the same lock `loadMore` takes.
   * A poll during an append is dropped, not queued: a refetch landing
   * mid-append rewrites the cache from page one.
   */
  refresh: (run: () => Promise<unknown>) => Promise<void>;
}

/**
 * `fetchMore` takes only the cursor because the caller owns the filter and sort
 * variables; pass it a memoized callback, since `loadMore`'s identity follows
 * it into every dependency array it lands in.
 */
export function useConnectionPagination<TNode>(opts: {
  connection: PaginatedConnection<TNode> | null | undefined;
  loading: boolean;
  fetchMore: (after: string) => Promise<unknown>;
}): ConnectionPagination<TNode> {
  const { connection, loading, fetchMore } = opts;

  const edges = connection?.edges;
  const rows = React.useMemo(() => (edges ?? []).map((edge) => edge.node), [edges]);
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;
  const totalCount = connection?.totalCount ?? 0;

  const inFlightRef = React.useRef(false);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);

  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage || loading || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsFetchingMore(true);
    void fetchMore(endCursor)
      .catch(() => undefined)
      .finally(() => {
        inFlightRef.current = false;
        setIsFetchingMore(false);
      });
  }, [endCursor, hasNextPage, loading, fetchMore]);

  const refresh = React.useCallback(async (run: () => Promise<unknown>) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      await run();
    } catch {
      // Swallowed: nothing on screen changes, and the next tick retries.
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  return { rows, hasNextPage, totalCount, loadMore, isFetchingMore, refresh };
}
